/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */
import { useEffect, type Dispatch, type SetStateAction } from 'react';
import { BuildingType, RandomEvent } from '../types';
import { GRID_SIZE } from '../constants';
import { resolvePlayAudio } from '../src/adapters/ui/hooks/useAudio';
import type { ActiveModal } from '../components/UIOverlay';

export interface UseGameKeyboardDeps {
  gameStarted: boolean;
  activeModal: ActiveModal;
  activeEvent: RandomEvent | null;
  selectedTool: BuildingType;
  selectedTile: { x: number; y: number } | null;
  setActiveModal: (modal: ActiveModal) => void;
  setGameSpeed: Dispatch<SetStateAction<number>>;
  setSelectedTool: Dispatch<SetStateAction<BuildingType>>;
  setSelectedTile: Dispatch<SetStateAction<{ x: number; y: number } | null>>;
}

export function useGameKeyboard({
  gameStarted,
  activeModal,
  activeEvent,
  selectedTool,
  selectedTile,
  setActiveModal,
  setGameSpeed,
  setSelectedTool,
  setSelectedTile,
}: UseGameKeyboardDeps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!gameStarted) return;

      if (e.key === 'Escape') {
        if (activeModal !== 'none') {
          setActiveModal('none');
          resolvePlayAudio({ type: 'sfx', sound: 'click' });
          return;
        }
        if (selectedTile) {
          setSelectedTile(null);
          return;
        }
        if (selectedTool !== BuildingType.None) {
          setSelectedTool(BuildingType.None);
          return;
        }
      }

      if (e.key.toLowerCase() === 'b' && activeModal === 'none') {
        resolvePlayAudio({ type: 'sfx', sound: 'click' });
        setSelectedTool(BuildingType.None);
        setSelectedTile(null);
      }

      if (e.key.toLowerCase() === 'r' && activeModal === 'none') {
        resolvePlayAudio({ type: 'sfx', sound: 'click' });
        setActiveModal('dashboard');
      }

      if (e.key.toLowerCase() === 'e' && activeModal === 'none') {
        resolvePlayAudio({ type: 'sfx', sound: 'click' });
        setActiveModal('soc');
      }

      if (e.key.toLowerCase() === 'p' && activeModal === 'none') {
        resolvePlayAudio({ type: 'sfx', sound: 'click' });
        setActiveModal('policyManagement');
      }

      if (e.code === 'Space' && activeModal === 'none' && !activeEvent) {
        e.preventDefault();
        resolvePlayAudio({ type: 'sfx', sound: 'click' });
        setGameSpeed((prev) => (prev === 0 ? 1 : 0));
      }

      if (
        activeModal === 'none' &&
        ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'a', 's', 'd'].includes(e.key)
      ) {
        if (['ArrowUp', 'ArrowDown'].includes(e.key)) e.preventDefault();

        setSelectedTile((prev) => {
          let { x, y } = prev || { x: Math.floor(GRID_SIZE / 2), y: Math.floor(GRID_SIZE / 2) };

          if (e.key === 'ArrowUp' || e.key === 'w') y -= 1;
          if (e.key === 'ArrowDown' || e.key === 's') y += 1;
          if (e.key === 'ArrowLeft' || e.key === 'a') x -= 1;
          if (e.key === 'ArrowRight' || e.key === 'd') x += 1;

          x = Math.max(0, Math.min(GRID_SIZE - 1, x));
          y = Math.max(0, Math.min(GRID_SIZE - 1, y));

          return { x, y };
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    gameStarted,
    setSelectedTile,
    setSelectedTool,
    setActiveModal,
    setGameSpeed,
    activeModal,
    activeEvent,
    selectedTool,
    selectedTile,
  ]);
}